import { __decorate, __metadata } from "tslib";
import { I18nThemed } from 'interfaces/mixins';
import { theme } from '@dojo/framework/core/mixins/Themed';
import { v, w } from '@dojo/framework/core/vdom';
import { beforeProperties } from '@dojo/framework/core/decorators/beforeProperties';
import { formatAriaProperties } from '@dojo/widgets/common/util';
import Button from '@dojo/widgets/button';
import { lccstore } from 'lcc-state';
import css from '../css/pretest-review.m.css';
let PretestReview = class PretestReview extends I18nThemed {
    data = [];
    _inited = false;
    onAttach() {
        lccstore.onChange(lccstore.path('dynls'), () => this.invalidate());
    }
    ensure_nls(properties) {
        const { get, path } = lccstore;
        const dynls = get(path('dynls'));
        const bundle = dynls.get('quiz');
        if (!bundle) {
            properties.localizedBundle = { isPlaceholder: true, format: null, messages: {} };
        }
        else {
            properties.localizedBundle = this.localizeBundle(bundle);
        }
        (() => {
            const { quizstore } = properties;
            const { get, path } = quizstore;
            properties.loaded = get(path('loaded'));
            if (!this._inited) {
                const invalidatePaths = [['loaded']].map((path) => quizstore.path(path.join('/')));
                const handle = quizstore.onChange(invalidatePaths, () => this.invalidate());
                this.own({
                    destroy: () => {
                        handle.remove();
                    }
                });
                this._inited = true;
            }
        })();
        return properties;
    }
    render_heads() {
        const { localizedBundle: { messages: { question: questionText = '', incorrect: incorrectText = '', status: statusText = '' } } } = this.properties;
        return v('div', { key: 'head-block', role: 'rowgroup', classes: [this.theme(css.tableHead), this.theme(css.row)] }, [
            v('div', { role: 'row' }, [
                v('th', {
                    key: 'head-question',
                    role: 'columnheader',
                    'aria-sort': 'none',
                    classes: [this.theme(css.question)]
                }, [questionText]),
                v('th', {
                    key: 'head-marked',
                    role: 'columnheader',
                    'aria-sort': 'none',
                    classes: [this.theme(css.marked)]
                }, [incorrectText]),
                v('th', {
                    key: 'head-status',
                    role: 'columnheader',
                    'aria-sort': 'none',
                    classes: [this.theme(css.status)]
                }, [statusText])
            ])
        ]);
    }
    render_row(question) {
        const { filterOn, onReview, localizedBundle: { messages: { question: questionText = '', incorrect: incorrectText = '', status_complete = '[quiz] status_complete', status_incomplete = '[quiz] status_incomplete' } } } = this.properties;
        const { cursor, marked = false, flags } = question;
        const isOdd = !!(cursor % 2);
        const isComplete = flags[1];
        const isMarked = marked;
        return v('div', { role: 'row' }, [
            v('a', {
                key: `row-${cursor}`,
                href: 'javascript:void;',
                classes: [this.theme(css.row), isOdd ? this.theme(css.oddRow) : null],
                onclick: (event) => {
                    event && event.stopPropagation();
                    filterOn(cursor);
                    onReview(false);
                }
            }, [
                v('div', { key: `q-${cursor}`, role: 'cell', classes: [this.theme(css.question)] }, [
                    `${questionText} ${cursor + 1}`
                ]),
                v('div', { key: `mrk-${cursor}`, role: 'cell', classes: [this.theme(css.marked)] }, [
                    isMarked
                        ? v('i', {
                            ...formatAriaProperties({ label: incorrectText }),
                            classes: ['fa', 'fa-check']
                        })
                        : v('i', {}, [' '])
                ]),
                v('div', {
                    role: 'cell button',
                    key: `cmp-${cursor}`,
                    classes: [
                        this.theme(css.status),
                        isComplete ? this.theme(css.complete) : this.theme(css.incomplete)
                    ]
                }, [
                    isComplete
                        ? v('i', {
                            ...formatAriaProperties({ label: status_complete }),
                            classes: ['fa', 'fa-circle']
                        })
                        : v('i', {
                            ...formatAriaProperties({ label: status_incomplete }),
                            classes: ['fa', 'fa-exclamation-circle']
                        })
                ])
            ])
        ]);
    }
    render_buttons() {
        const { onFinish, filterOn, checkFor, onReview, localizedBundle: { messages: { review_all: reviewAllText = '', review_incorrect: reviewIncorrectText = '', review_incomplete: reviewIncompleteText = '', end_review: finishText = '' } } } = this.properties;
        return [
            v('div', { classes: [this.theme(css.oneaction)] }, [
                w(Button, {
                    key: 'all',
                    onClick: () => {
                        filterOn('all');
                        onReview(false);
                    }
                }, [reviewAllText])
            ]),
            v('div', { classes: [this.theme(css.oneaction)] }, [
                w(Button, {
                    key: 'marked',
                    disabled: !checkFor('marked'),
                    onClick: () => {
                        filterOn('marked');
                        onReview(false);
                    }
                }, [reviewIncorrectText])
            ]),
            v('div', { classes: [this.theme(css.oneaction)] }, [
                w(Button, {
                    key: 'incomplete',
                    disabled: !checkFor('incomplete'),
                    onClick: () => {
                        filterOn('incomplete');
                        onReview(false);
                    }
                }, [reviewIncompleteText])
            ]),
            v('div', { classes: [this.theme(css.oneaction)] }, [
                w(Button, {
                    key: 'finish',
                    onClick: () => {
                        onFinish();
                    }
                }, [finishText])
            ]),
            v('div', { key: 'timer', classes: [this.theme(css.timerblock)] }, [])
        ];
    }
    render_review() {
        const { getQuestions, loaded = false, localizedBundle: { messages: { review_instructions = '' } } } = this.properties;
        if (!loaded) {
            v('div', { key: 'loading', classes: [this.theme(css.loading)] }, [
                v('i', { classes: ['fas', 'fa-spinner', 'fa-spin'] })
            ]);
        }
        if (this.data.length === 0) {
            this.data = getQuestions();
        }
        const { data } = this;
        const kids = data.map((q) => this.render_row(q));
        return [
            v('div', { key: 'buttons', classes: [this.theme(css.buttons)] }, this.render_buttons()),
            v('div', { key: 'instruct', classes: [this.theme(css.instruct)] }, [review_instructions]),
            v('div', { key: 'review-table', role: 'table', classes: [this.theme(css.table)], tabindex: '0' }, [
                this.render_heads(),
                v('div', { role: 'rowgroup' }, [...kids])
            ])
        ];
    }
    render() {
        const { isPlaceholder } = this.properties.localizedBundle;
        if (isPlaceholder) {
            return null;
        }
        const xqa = `review pretest-audit`;
        return v('div', { key: 'review', xqa, classes: [this.theme(css.root)] }, this.render_review());
    }
};
__decorate([
    beforeProperties(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", Object)
], PretestReview.prototype, "ensure_nls", null);
PretestReview = __decorate([
    theme(css)
], PretestReview);
export { PretestReview };
export default PretestReview;
